import * as React from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Box, Typography, Link } from "@mui/material";
import AuthLayout from "../../components/AuthLayout";
import EmailStepForm from "../../components/EmailStepForm";
import OtpBoxes from "../../components/OtpBoxes";
import SetNewPasswordForm from "../../components/SetNewPasswordForm";
import AuthPrimaryButton from "../../components/AuthPrimaryButton";
import ExpiredLinkScreen from "../../components/ExpiredLinkScreen";
import {
  forgotPassword,
  resetPasswordConfirmCode,
  resetPasswordComplete,
} from "../../Services/authService";
import { AUTH_TEXT_PRIMARY, AUTH_TEXT_SECONDARY, AUTH_ERROR, AUTH_SUCCESS, BRAND_BLUE } from "../../theme/appTheme";
import { useTranslation } from "../../i18n/LanguageContext";

// Password-reset flow: email -> enter the 6-digit code from the email -> set a new password.
function ForgotPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [step, setStep] = React.useState("email"); // email | code | password | expired | done | needsRegistration | notFound
  const [email, setEmail] = React.useState("");
  const [confirmationId, setConfirmationId] = React.useState(null);
  const [code, setCode] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [fieldErrors, setFieldErrors] = React.useState([]);
  const [resent, setResent] = React.useState(false);

  const resetToStart = () => {
    setStep("email");
    setEmail("");
    setConfirmationId(null);
    setCode("");
    setError("");
  };

  const startFor = async (submittedEmail) => {
    // Backend flags the two cases where no email is actually sent —
    // accountNotFound (no user row) and accountNeedsRegistration (a shadow
    // account: has orders, never set a password) — so the UI can send the
    // user somewhere useful instead of a code screen that will never fill.
    const data = await forgotPassword(submittedEmail);
    setEmail(submittedEmail);
    setConfirmationId(data.confirmationId || null);
    return data;
  };

  const handleStart = async (submittedEmail) => {
    setLoading(true);
    setError("");
    try {
      const data = await startFor(submittedEmail);
      if (data.accountNotFound) {
        setStep("notFound");
      } else if (data.accountNeedsRegistration) {
        setStep("needsRegistration");
      } else {
        setStep("code");
      }
    } catch (err) {
      if (err.status === 429) {
        setError(t("auth.forgotPassword.errors.tooMany"));
      } else {
        setError(err.message || t("auth.forgotPassword.errors.generic"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await startFor(email);
      if (data.accountNotFound) {
        setStep("notFound");
        return;
      }
      if (data.accountNeedsRegistration) {
        setStep("needsRegistration");
        return;
      }
      setCode("");
      setResent(true);
      setTimeout(() => setResent(false), 3000);
    } catch (err) {
      setError(err.message || t("auth.forgotPassword.code.resendError"));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmCode = async () => {
    if (!/^\d{6}$/.test(code)) return;
    setLoading(true);
    setError("");
    try {
      await resetPasswordConfirmCode(confirmationId, email, code);
      setStep("password");
    } catch (err) {
      if (err.status === 400 && /invalid or has expired/i.test(err.message)) {
        setStep("expired");
      } else if (err.status === 429) {
        setError(t("auth.forgotPassword.code.tooMany"));
      } else {
        setError(t("auth.forgotPassword.code.incorrectCode"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (password) => {
    setLoading(true);
    setError("");
    setFieldErrors([]);
    try {
      await resetPasswordComplete(confirmationId, email, password);
      setStep("done");
    } catch (err) {
      if (err.status === 400 && Array.isArray(err.errors) && err.errors.length) {
        setFieldErrors(err.errors);
      } else if (err.status === 400 && /invalid or has expired/i.test(err.message)) {
        setStep("expired");
      } else if (err.status === 429) {
        setError(t("auth.forgotPassword.completeErrors.tooMany"));
      } else {
        setError(err.message || t("auth.forgotPassword.completeErrors.generic"));
      }
    } finally {
      setLoading(false);
    }
  };

  if (step === "expired") {
    return <ExpiredLinkScreen onRetry={resetToStart} />;
  }

  return (
    <AuthLayout>
      {step === "needsRegistration" && (
        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ fontWeight: 600, mb: 1, fontSize: "1.0625rem", color: AUTH_TEXT_PRIMARY }}>
            {t("auth.forgotPassword.needsRegistration.title")}
          </Typography>
          <Typography sx={{ mb: 3, fontSize: "0.8125rem", color: AUTH_TEXT_SECONDARY }}>
            {t("auth.forgotPassword.needsRegistration.subtitle")}
          </Typography>
          <AuthPrimaryButton onClick={() => navigate("/register")} sx={{ px: 4, width: "auto" }}>
            {t("auth.forgotPassword.needsRegistration.cta")}
          </AuthPrimaryButton>
        </Box>
      )}

      {step === "notFound" && (
        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ fontWeight: 600, mb: 1, fontSize: "1.0625rem", color: AUTH_TEXT_PRIMARY }}>
            {t("auth.forgotPassword.notFound.title")}
          </Typography>
          <Typography sx={{ mb: 3, fontSize: "0.8125rem", color: AUTH_TEXT_SECONDARY }}>
            {t("auth.forgotPassword.notFound.subtitle")}
          </Typography>
          <AuthPrimaryButton onClick={() => navigate("/register")} sx={{ px: 4, width: "auto" }}>
            {t("auth.forgotPassword.notFound.cta")}
          </AuthPrimaryButton>
          <Typography sx={{ mt: 2, fontSize: "0.75rem", color: AUTH_TEXT_SECONDARY }}>
            <Link component="button" type="button" onClick={resetToStart} sx={{ color: BRAND_BLUE, fontSize: "0.75rem" }}>
              {t("auth.forgotPassword.notFound.tryDifferentEmail")}
            </Link>
          </Typography>
        </Box>
      )}

      {step === "email" && (
        <EmailStepForm
          title={t("auth.forgotPassword.title")}
          subtitle={t("auth.forgotPassword.subtitle")}
          submitLabel={t("auth.forgotPassword.submit")}
          onSubmit={handleStart}
          loading={loading}
          error={error}
          footer={
            <Typography sx={{ fontSize: "0.8125rem", color: AUTH_TEXT_SECONDARY }}>
              {t("auth.forgotPassword.rememberedIt")}{" "}
              <Link component={RouterLink} to="/login" sx={{ color: BRAND_BLUE }}>
                {t("auth.forgotPassword.signIn")}
              </Link>
            </Typography>
          }
        />
      )}

      {step === "code" && (
        <Box>
          <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: "1.0625rem", color: AUTH_TEXT_PRIMARY, textAlign: "center" }}>
            {t("auth.forgotPassword.code.title")}
          </Typography>
          <Typography sx={{ mb: 3, fontSize: "0.8125rem", color: AUTH_TEXT_SECONDARY, textAlign: "center" }}>
            {t("auth.forgotPassword.code.subtitle").split("{{email}}")[0]}
            <strong>{email}</strong>
            {t("auth.forgotPassword.code.subtitle").split("{{email}}")[1]}
          </Typography>

          <OtpBoxes value={code} onChange={setCode} error={!!error} />

          {error && (
            <Typography variant="caption" sx={{ color: AUTH_ERROR, mt: 2, display: "block", textAlign: "center" }}>
              {error}
            </Typography>
          )}
          {resent && !error && (
            <Typography variant="caption" sx={{ color: AUTH_SUCCESS, mt: 2, display: "block", textAlign: "center" }}>
              {t("auth.forgotPassword.code.resent")}
            </Typography>
          )}

          <AuthPrimaryButton onClick={handleConfirmCode} disabled={loading || !/^\d{6}$/.test(code)} sx={{ mt: 3 }}>
            {loading ? t("auth.forgotPassword.code.checking") : t("auth.forgotPassword.code.verify")}
          </AuthPrimaryButton>

          <Typography sx={{ mt: 2.5, textAlign: "center", fontSize: "0.75rem", color: AUTH_TEXT_SECONDARY }}>
            {t("auth.forgotPassword.code.notSeeing")}{" "}
            <Link component="button" type="button" onClick={handleResend} sx={{ color: BRAND_BLUE, fontSize: "0.75rem" }}>
              {t("auth.forgotPassword.code.tryAgain")}
            </Link>
            .{" "}
            {t("auth.forgotPassword.code.wrongEmail")}{" "}
            <Link component="button" type="button" onClick={resetToStart} sx={{ color: BRAND_BLUE, fontSize: "0.75rem" }}>
              {t("auth.forgotPassword.code.changeEmail")}
            </Link>
            .
          </Typography>
        </Box>
      )}

      {step === "password" && (
        <SetNewPasswordForm onSubmit={handleComplete} loading={loading} error={error} fieldErrors={fieldErrors} />
      )}

      {step === "done" && (
        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ fontWeight: 600, mb: 1, fontSize: "1.0625rem", color: AUTH_TEXT_PRIMARY }}>
            {t("auth.forgotPassword.done.title")}
          </Typography>
          <Typography sx={{ mb: 3, fontSize: "0.8125rem", color: AUTH_TEXT_SECONDARY }}>
            {t("auth.forgotPassword.done.subtitle")}
          </Typography>
          <Link component="button" type="button" onClick={() => navigate("/login")} sx={{ color: BRAND_BLUE }}>
            {t("auth.forgotPassword.done.goToSignIn")}
          </Link>
        </Box>
      )}
    </AuthLayout>
  );
}

export default ForgotPassword;
